/**
 * Billing: the workspace's plan, what it has used against it, and the way to
 * a bigger one.
 *
 * Buying and managing happen on Stripe's own pages: the console asks the API
 * for a Checkout or portal URL and sends the browser there, and Stripe sends
 * it back to `?billing=…#/billing`. The console never sees a card. When the
 * deployment has no Stripe configured the API says 503, and this page says
 * so plainly rather than offering buttons that cannot work.
 */

import type { Answer, Api } from './api';
import { Notices, h, mount } from './dom';

// -- what the API returns ----------------------------------------------------------------------

export interface PlanLimits {
  readonly requestsPerMinute: number;
  readonly keyRequestsPerMinute: number;
  readonly maxWorlds: number;
  readonly monthlyWrites: number;
}
export interface PlanView { readonly id: string; readonly name: string; readonly limits: PlanLimits }
export interface BillingState {
  readonly workspace: string;
  /** False: Stripe is not configured on this deployment, and nothing can be bought. */
  readonly configured: boolean;
  /** `mock`: no Stripe behind it; the console's mock screens stand in for Checkout and the portal. */
  readonly mode?: 'stripe' | 'mock' | null;
  readonly plan: PlanView;
  readonly customer: boolean;
  readonly subscription: {
    readonly status: string;
    readonly plan: string | null;
    readonly currentPeriodEnd: string | null;
    readonly cancelAtPeriodEnd: boolean;
    readonly paymentFailedAt: string | null;
  } | null;
  readonly plans: ReadonlyArray<PlanView & { readonly buyable: boolean }>;
}
export interface UsageState {
  readonly period: { readonly id: string; readonly start: string; readonly end: string };
  readonly usage: { readonly requests: number; readonly writes: number; readonly worlds: number };
  readonly plan: PlanView;
}

export const billingOf = (api: Api): Promise<Answer<BillingState>> => api.request('GET', '/v1/billing');
export const usageOf = (api: Api): Promise<Answer<UsageState>> => api.request('GET', '/v1/usage');
export const checkout = (api: Api, plan: string): Promise<Answer<{ url: string; id: string }>> =>
  api.request('POST', '/v1/billing/checkout', { plan });
export const portal = (api: Api): Promise<Answer<{ url: string }>> => api.request('POST', '/v1/billing/portal', {});

export type MockAct = 'complete' | 'switch' | 'cancel' | 'fail' | 'recover';
export interface MockOutcome {
  readonly plan: string;
  readonly planName: string;
  readonly subscription: { readonly status: string | null };
}
/** An act at mock checkout or in the mock portal, which the API turns into the Stripe event it stands for. */
export const mockAct = (api: Api, act: MockAct, plan?: string): Promise<Answer<MockOutcome>> =>
  api.request('POST', `/v1/billing/mock/${act}`, plan ? { plan } : {});

// -- what the page offers ------------------------------------------------------------------------

/** A subscription in these is over; in any other it still stands, and changes go through the portal. */
const OVER = ['canceled', 'incomplete_expired'];

export interface Offer {
  /** Plans a button may buy: for sale, not the one held, and no subscription standing. */
  readonly upgrades: readonly PlanView[];
  /** A Stripe customer exists, so the portal can open. */
  readonly manage: boolean;
  /** Why nothing can be bought here, when that is so. */
  readonly closed: string | null;
}

export const offerOf = (b: BillingState): Offer => {
  const stands = b.subscription !== null && !OVER.includes(b.subscription.status);
  const closed = !b.configured
    ? 'Billing is not configured on this deployment: plans can\'t be bought here yet.'
    : b.plan.id === 'internal'
      ? 'This workspace is on the internal plan, which is never billed.'
      : null;
  return {
    upgrades: closed || stands ? [] : b.plans.filter((p) => p.buyable && p.id !== b.plan.id),
    manage: b.configured && b.customer,
    closed,
  };
};

/** What Stripe sent the browser back with (`?billing=…`), as a sentence; null when it sent nothing. */
export const returnNote = (search: string): { ok: boolean; text: string } | null => {
  switch (new URLSearchParams(search).get('billing')) {
    case 'done': return { ok: true, text: 'Payment received. The new plan shows here once Stripe confirms it, and its limits apply within a minute.' };
    case 'cancelled': return { ok: false, text: 'Checkout was cancelled; nothing was charged.' };
    case 'portal': return { ok: true, text: 'Back from the billing portal. Changes show here once Stripe confirms them.' };
    default: return null;
  }
};

// -- the view ------------------------------------------------------------------------------------

const count = (n: number): string => n.toLocaleString('en-US');
const day = (iso: string | null): string => (iso ? iso.slice(0, 10) : '—');
const statusClass = (status: string): string =>
  status === 'active' || status === 'trialing' ? 's-held' : status === 'past_due' || status === 'incomplete' ? 's-asked' : 's-refused';

export async function renderBilling(view: HTMLElement, api: Api): Promise<void> {
  const notices = new Notices();
  const body = h('div', { class: 'stack' }, h('p', { class: 'hint' }, 'Loading…'));
  mount(view,
    h('section', {},
      h('div', { class: 'page-head' }, h('h2', {}, 'Billing'),
        h('p', { class: 'lede' }, 'This workspace\'s plan, what it has used this month, and the way to a bigger one. Payment happens on Stripe\'s pages.')),
      notices.el,
      body));

  const back = returnNote(location.search);
  if (back) {
    if (back.ok) notices.ok(back.text);
    else notices.show({ type: 'about:blank', title: 'Cancelled', status: 400, detail: back.text });
    // Drop the query, so a reload does not say it again.
    history.replaceState(null, '', `${location.pathname}${location.hash}`);
  }

  const [billing, usage] = await Promise.all([billingOf(api), usageOf(api)]);
  const b = notices.take(billing);
  if (!b) return mount(body);
  const used = usage.ok ? usage.value : null;
  const offer = offerOf(b);
  const limits = b.plan.limits;

  const closedNote = h('div', {}, offer.closed ? h('div', { class: 'notice error' }, h('p', { class: 'detail' }, offer.closed)) : null);

  /** Go to Stripe, or say why not; a 503 means billing is off, which the page then shows. */
  const leave = async (asked: Promise<Answer<{ url: string }>>, button: HTMLButtonElement) => {
    button.disabled = true;
    const answer = await asked;
    if (answer.ok) {
      location.assign(answer.value.url);
      return;
    }
    button.disabled = false;
    if (answer.problem.status === 503) {
      mount(closedNote, h('div', { class: 'notice error' }, h('p', { class: 'detail' }, answer.problem.detail)));
      return;
    }
    notices.show(answer.problem);
  };

  const current = h('div', { class: 'card' },
    h('h3', {}, `${b.plan.name} `, h('span', { class: 'mono muted' }, b.plan.id)),
    h('table', {},
      h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', {}, 'Used'), h('th', {}, 'Limit'))),
      h('tbody', {},
        h('tr', {}, h('td', {}, `Writes (${used?.period.id ?? 'this month'})`),
          h('td', { class: 'mono' }, used ? count(used.usage.writes) : '—'), h('td', { class: 'mono' }, count(limits.monthlyWrites))),
        h('tr', {}, h('td', {}, 'Requests this month'),
          h('td', { class: 'mono' }, used ? count(used.usage.requests) : '—'), h('td', { class: 'mono muted' }, 'not capped')),
        h('tr', {}, h('td', {}, 'Worlds'),
          h('td', { class: 'mono' }, used ? count(used.usage.worlds) : '—'), h('td', { class: 'mono' }, count(limits.maxWorlds))),
        h('tr', {}, h('td', {}, 'Requests a minute (workspace · one key)'),
          h('td', {}, ''), h('td', { class: 'mono' }, `${count(limits.requestsPerMinute)} · ${count(limits.keyRequestsPerMinute)}`)))),
    usage.ok ? null : h('p', { class: 'hint' }, `Usage unavailable: ${usage.problem.detail}`));

  const sub = b.subscription;
  const subscription = h('div', { class: 'card' },
    h('h3', {}, 'Subscription'),
    sub
      ? h('dl', { class: 'facts' },
        h('dt', {}, 'Status'), h('dd', {}, h('span', { class: `badge ${statusClass(sub.status)}` }, sub.status)),
        h('dt', {}, sub.cancelAtPeriodEnd || OVER.includes(sub.status) ? 'Ends' : 'Renews'), h('dd', { class: 'mono' }, day(sub.currentPeriodEnd)),
        sub.paymentFailedAt ? [h('dt', {}, 'Payment failing since'), h('dd', { class: 'mono' }, day(sub.paymentFailedAt))] : null)
      : h('p', { class: 'hint' }, 'None. This workspace pays nothing.'),
    sub?.paymentFailedAt && !OVER.includes(sub.status)
      ? h('p', { class: 'hint' }, 'Stripe is retrying the card. The plan stays until Stripe gives up; update the card in the billing portal.')
      : null,
    offer.manage
      ? h('button', {
        class: 'primary',
        onclick: (e: Event) => void leave(portal(api), e.currentTarget as HTMLButtonElement),
      }, 'Manage billing')
      : null);

  /** A plan row's last cell: the current badge, a button to buy it, or why there is none. */
  const choice = (p: BillingState['plans'][number]): HTMLElement => {
    if (p.id === b.plan.id) return h('span', { class: 'badge s-held' }, 'current');
    if (offer.upgrades.some((u) => u.id === p.id)) {
      return h('button', { class: 'small', onclick: (e: Event) => void leave(checkout(api, p.id), e.currentTarget as HTMLButtonElement) }, `Choose ${p.name}`);
    }
    const why = !p.buyable ? (p.id === 'beta' ? '' : 'not for sale') : sub && !OVER.includes(sub.status) ? 'via Manage billing' : '';
    return h('span', { class: 'muted' }, why);
  };

  const plans = h('div', { class: 'card' },
    h('h3', {}, 'Plans'),
    h('table', {},
      h('thead', {}, h('tr', {}, h('th', {}, 'Plan'), h('th', {}, 'Writes / month'), h('th', {}, 'Worlds'), h('th', {}, 'Req / min'), h('th', {}, ''))),
      h('tbody', {}, ...b.plans.map((p) => h('tr', {},
        h('td', {}, p.name),
        h('td', { class: 'mono' }, count(p.limits.monthlyWrites)),
        h('td', { class: 'mono' }, count(p.limits.maxWorlds)),
        h('td', { class: 'mono' }, count(p.limits.requestsPerMinute)),
        h('td', {}, choice(p)))))),
    h('p', { class: 'hint' }, 'A new plan\'s limits apply within a minute of Stripe confirming the payment.'));

  const mockNote = b.mode === 'mock'
    ? h('div', { class: 'notice' }, h('p', { class: 'detail' },
      'Mock billing: this deployment has no Stripe account yet. Checkout and "Manage billing" open stand-in screens, no card is charged, and the plan changes exactly as a real payment would change it.'))
    : null;
  mount(body, mockNote, closedNote, h('div', { class: 'columns' }, h('div', { class: 'stack' }, current, plans), subscription));
}

// -- the mock's stand-ins for Stripe's pages ----------------------------------------------------

/** Mock checkout: what Stripe Checkout would be, minus the card. */
export function renderMockCheckout(view: HTMLElement, api: Api, plan: string | null): void {
  const notices = new Notices();
  const pay = async (button: HTMLButtonElement) => {
    button.disabled = true;
    const answer = await mockAct(api, 'complete', plan ?? '');
    if (answer.ok) {
      location.assign(`${location.pathname}?billing=done#/billing`);
      return;
    }
    button.disabled = false;
    notices.show(answer.problem);
  };
  mount(view,
    h('section', {},
      h('div', { class: 'page-head' }, h('h2', {}, 'Mock checkout'),
        h('p', { class: 'lede' }, 'Stands in for Stripe Checkout while there is no Stripe account. No card is asked for or charged.')),
      notices.el,
      h('div', { class: 'card' },
        h('h3', {}, 'Subscribe to ', h('span', { class: 'mono' }, plan ?? '(no plan)')),
        h('p', { class: 'hint' }, 'Paying here sends the API the event Stripe would send after a real payment, so the workspace moves to this plan the same way.'),
        h('div', { class: 'row' },
          h('button', { class: 'primary', onclick: (e: Event) => void pay(e.currentTarget as HTMLButtonElement) }, 'Pay (mock)'),
          h('button', { onclick: () => location.assign(`${location.pathname}?billing=cancelled#/billing`) }, 'Cancel')))));
}

/** Mock portal: what Stripe's customer portal would let the customer do, plus the card failing and recovering. */
export async function renderMockPortal(view: HTMLElement, api: Api): Promise<void> {
  const notices = new Notices();
  const body = h('div', { class: 'stack' }, h('p', { class: 'hint' }, 'Loading…'));
  mount(view,
    h('section', {},
      h('div', { class: 'page-head' }, h('h2', {}, 'Mock billing portal'),
        h('p', { class: 'lede' }, 'Stands in for Stripe\'s customer portal. Each button sends the API the event Stripe would send.')),
      notices.el,
      body));

  const act = async (what: MockAct, plan: string | undefined, button: HTMLButtonElement) => {
    button.disabled = true;
    const answer = await mockAct(api, what, plan);
    if (answer.ok) {
      notices.ok(`Now on ${answer.value.planName} (${answer.value.subscription.status ?? 'no subscription'}).`);
      return void renderMockPortal(view, api);
    }
    button.disabled = false;
    notices.show(answer.problem);
  };
  const billing = notices.take(await billingOf(api));
  if (!billing) return mount(body);
  const sub = billing.subscription;
  const live = sub && !OVER.includes(sub.status);
  const button = (label: string, what: MockAct, plan?: string, cls = 'small') =>
    h('button', { class: cls, onclick: (e: Event) => void act(what, plan, e.currentTarget as HTMLButtonElement) }, label);

  mount(body,
    h('div', { class: 'card' },
      h('h3', {}, `${billing.plan.name} `, sub ? h('span', { class: `badge ${statusClass(sub.status)}` }, sub.status) : null),
      live
        ? h('div', { class: 'stack' },
          h('div', { class: 'row' }, ...billing.plans.filter((p) => p.buyable && p.id !== billing.plan.id).map((p) => button(`Switch to ${p.name}`, 'switch', p.id))),
          h('div', { class: 'row' },
            sub!.status === 'past_due' ? button('Card goes through', 'recover') : button('Card is declined', 'fail'),
            button('Cancel subscription', 'cancel', undefined, 'small danger')))
        : h('p', { class: 'hint' }, 'No subscription stands. Buy a plan from Billing.')),
    h('p', {}, h('a', { href: '#/billing' }, '← Back to Billing')));
}
